import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Check, Sparkles, Leaf, Clock, MapPin, Quote, ArrowRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ConciergeChat from "@/components/site/ConciergeChat";
import { useAuth } from "@/lib/auth";

const HERO = "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?auto=format&fit=crop&q=80&w=2400";
const SHOT_1 = "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&q=80&w=1200";
const SHOT_2 = "https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?auto=format&fit=crop&q=80&w=1200";
const SHOT_3 = "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&q=80&w=1200";

function useReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const els = ref.current?.querySelectorAll(".reveal") ?? [];
        els.forEach((el) => {
            const r = el.getBoundingClientRect();
            if (r.top < window.innerHeight && r.bottom > 0) el.classList.add("in");
        });
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
            { threshold: 0.08, rootMargin: "0px 0px -10% 0px" }
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);
    return ref;
}

export default function Facials() {
    const ref = useReveal();
    const { user } = useAuth();
    const cta = user ? "/portal/book" : "/login";

    const treatments = [
        {
            name: "Signature Glow Facial",
            duration: "45 min",
            desc: "Deep cleanse, exfoliation, custom mask, hydration. Matched to your skin's mood.",
            for: "Every skin type · monthly maintenance",
        },
        {
            name: "Brightening + Anti-Aging",
            duration: "60 min",
            desc: "Vitamin C serum, lifting massage, peptide mask. Targets fine lines and dullness.",
            for: "30s+ · sun damage · pre-event glow",
        },
        {
            name: "Acne Calming Treatment",
            duration: "60 min",
            desc: "Salicylic clarifying steam, extractions, blue-light healing, calming gel mask.",
            for: "Breakouts · congested skin · sensitive",
        },
        {
            name: "Bridal / Pre-Event Facial",
            duration: "75 min",
            desc: "The full glow ritual — exfoliation, massage, mask, finishing oil. Camera-ready skin.",
            for: "Weddings · birthdays · big nights",
        },
        {
            name: "Professional Makeup Application",
            duration: "60 min",
            desc: "Full-face airbrush or traditional, including lashes. Long-wear pigments, photo-finish.",
            for: "Events · bridal · group bookings",
        },
    ];

    return (
        <div ref={ref} className="bg-ink text-white relative overflow-hidden bg-grain">
            <Navbar />

            {/* HERO */}
            <section data-testid="facials-hero" className="relative min-h-[88vh] flex items-center pt-32 pb-20">
                <div className="absolute inset-0 z-0">
                    <img src={HERO} alt="Mobile facial treatment" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-ink/65 via-ink/55 to-ink" />
                    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />
                </div>
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 w-full">
                    <p className="reveal text-xs tracking-[0.4em] uppercase text-rose mb-6">
                        ✦ Facials & Makeup ✦ At your door
                    </p>
                    <h1 className="reveal font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl">
                        Skin that <span className="italic text-rose">behaves.</span>
                        <br />
                        Makeup that <span className="gold-underline">photographs.</span>
                    </h1>
                    <p className="reveal mt-8 max-w-2xl text-lg text-white/75 leading-relaxed">
                        Personalized facials and professional makeup applications, delivered in our Glam on Wheels mobile spa.
                        No spa traffic, no parking, no lobby small talk — just expertly tailored skin care and event-ready makeup, on your schedule.
                    </p>
                    <div className="reveal mt-10 flex flex-wrap gap-4">
                        <Link
                            to="/skin-quiz"
                            data-testid="facials-cta-quiz"
                            className="group px-8 py-4 rounded-full bg-gradient-to-r from-gold via-rose to-gold text-ink font-semibold hover:opacity-90 transition flex items-center gap-2 shadow-lg shadow-gold/20"
                        >
                            <Sparkles className="w-4 h-4" /> Take the Skin Quiz
                            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                        </Link>
                        <Link
                            to={cta}
                            data-testid="facials-cta-book"
                            className="group px-8 py-4 rounded-full border border-rose/50 text-white hover:bg-rose hover:text-ink transition flex items-center gap-2"
                        >
                            Book a Facial <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                        </Link>
                    </div>
                    <p className="reveal mt-4 text-xs text-white/45">
                        ✦ 2-minute personalized assessment · we match you to your perfect facial
                    </p>
                    <p className="reveal mt-3 text-xs tracking-[0.2em] uppercase text-white/45">
                        Same-day · vegan products · mobile · pro makeup artists on staff
                    </p>
                </div>
            </section>

            {/* PRINCIPLES */}
            <section data-testid="facials-principles" className="relative py-24 bg-surface border-y border-hairline">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid md:grid-cols-3 gap-6">
                    {[
                        { i: Leaf, t: "Vegan, never tested on animals", b: "Plant-derived actives (vitamin C, hyaluronic acid, niacinamide, peptides). No parabens, sulfates, mineral oil." },
                        { i: Clock, t: "Same-day, by appointment", b: "Book before 6pm and we're at your door today. Emergency-in-an-hour available for last-minute events." },
                        { i: MapPin, t: "Glam on Wheels", b: "A fully-equipped mobile spa van. Heated towels, sterilized tools, salon-grade everything — set up in your living room in minutes." },
                    ].map(({ i: Icon, t, b }, i) => (
                        <div key={i} className="reveal p-6 rounded-2xl border border-white/10">
                            <Icon className="w-6 h-6 text-rose" />
                            <p className="font-serif text-2xl mt-3">{t}</p>
                            <p className="text-sm text-white/60 mt-2 leading-relaxed">{b}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* TREATMENTS */}
            <section data-testid="facials-menu" className="relative py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <div className="reveal grid md:grid-cols-12 gap-8 items-end mb-16">
                        <div className="md:col-span-7">
                            <p className="text-xs tracking-[0.3em] uppercase text-gold">The menu</p>
                            <h2 className="font-serif text-5xl sm:text-6xl mt-3 leading-tight">
                                A treatment for every <span className="italic text-rose">mood, milestone, or moment.</span>
                            </h2>
                        </div>
                        <p className="md:col-span-5 text-white/65 text-lg">
                            Tell us what your skin is doing this week — we adapt every facial to you. No fixed protocols, no copy-paste menus.
                        </p>
                    </div>
                    <div className="reveal grid md:grid-cols-2 gap-6">
                        {treatments.map((t, i) => (
                            <div
                                key={t.name}
                                data-testid={`facial-${i}`}
                                className={`p-7 rounded-2xl border ${i === 4 ? "border-gold/40 bg-gold/5" : "border-white/10"} hover:border-rose/50 transition`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <h3 className="font-serif text-2xl">{t.name}</h3>
                                    <span className="text-xs tracking-widest uppercase text-white/50 mt-2 shrink-0">{t.duration}</span>
                                </div>
                                <p className="mt-3 text-white/65 leading-relaxed">{t.desc}</p>
                                <p className="mt-4 text-xs tracking-wide uppercase text-rose/80">{t.for}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* LED LIGHT MASK THERAPY */}
            <section data-testid="facials-led-therapy" className="relative py-32 bg-surface border-y border-hairline">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <div className="reveal grid lg:grid-cols-12 gap-10 items-center mb-16">
                        <div className="lg:col-span-7">
                            <p className="text-xs tracking-[0.3em] uppercase text-gold">New · LED Light Mask Therapy</p>
                            <h2 className="font-serif text-5xl sm:text-6xl mt-3 leading-tight">
                                Seven lights. <span className="italic text-rose">One glowing you.</span>
                            </h2>
                            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-2xl">
                                Our professional 7-color LED mask uses targeted wavelengths of light to refresh, calm,
                                and re-energize your skin — no needles, no downtime. Each 15–30 minute session starts
                                with a fresh, cleansed face and ends with that lit-from-within glow.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-4">
                                <Link
                                    to={cta}
                                    data-testid="facials-led-cta"
                                    className="px-7 py-3 rounded-full bg-gradient-to-r from-gold via-rose to-gold text-ink font-semibold hover:opacity-90 transition flex items-center gap-2"
                                >
                                    Book LED Therapy — $50 <ChevronRight className="w-4 h-4" />
                                </Link>
                                <span className="px-5 py-3 rounded-full border border-white/15 text-white/60 text-sm flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-rose" /> 15–30 min per session
                                </span>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="aspect-[3/4] rounded-3xl overflow-hidden border border-white/10 shadow-lg shadow-rose/10">
                                <img src="/gallery/led-mask.jpg" alt="LED light mask therapy session" className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>

                    <div className="reveal grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {[
                            { c: "#4D8DFF", name: "Blue", nm: "470 nm", desc: "Targets acne-causing bacteria and calms inflammation — gentle on skin, tough on breakouts." },
                            { c: "#4DDF7A", name: "Green", nm: "520 nm", desc: "Helps reduce excess oil and balance your skin's oil-and-water levels to minimize the look of swelling." },
                            { c: "#FFD24D", name: "Yellow", nm: "590 nm", desc: "Boosts cell oxygen flow, re-energizes skin cells, supports lymphatic detox, and softens fine lines." },
                            { c: "#3FE0D0", name: "Turquoise", nm: "Cyan blend", desc: "Gradually enhances cell energy and promotes healthy skin metabolism for a refreshed look." },
                            { c: "#B04DFF", name: "Purple", nm: "Red + blue", desc: "A two-in-one wavelength combining the clarifying power of blue with the renewing power of red." },
                            { c: "#FF5A5A", name: "Red", nm: "630 nm", desc: "The \"biologically active\" light — boosts collagen, speeds cell renewal, helps repair damaged skin, shrink pores, firm skin elasticity, and smooth wrinkles." },
                            { c: "#F5F0FF", name: "White", nm: "Full spectrum", desc: "Penetrates deepest to help reduce the appearance of age spots and dark spots for a more even tone." },
                        ].map((l) => (
                            <div key={l.name} data-testid={`led-light-${l.name.toLowerCase()}`} className="p-6 rounded-2xl border border-white/10 hover:border-rose/40 transition">
                                <div className="flex items-center gap-3">
                                    <span className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: l.c, boxShadow: `0 0 14px ${l.c}` }} />
                                    <h3 className="font-serif text-2xl">{l.name}</h3>
                                    <span className="ml-auto text-[11px] tracking-widest uppercase text-white/45">{l.nm}</span>
                                </div>
                                <p className="mt-3 text-sm text-white/65 leading-relaxed">{l.desc}</p>
                            </div>
                        ))}
                        <div className="p-6 rounded-2xl border border-gold/30 bg-gold/5">
                            <h3 className="font-serif text-2xl text-gold">Good to know</h3>
                            <ul className="mt-3 space-y-2 text-sm text-white/70 leading-relaxed">
                                <li className="flex gap-2"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> Cleanse your face before every session</li>
                                <li className="flex gap-2"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> 15–30 minutes per treatment</li>
                                <li className="flex gap-2"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> Pairs beautifully with any facial</li>
                            </ul>
                        </div>
                    </div>

                    <p data-testid="facials-led-safety" className="reveal mt-10 text-xs text-white/45 leading-relaxed max-w-3xl">
                        Not suitable if you are pregnant, under 12, have epilepsy, light sensitivity or light allergies,
                        or open skin wounds. LED therapy is a cosmetic treatment and is not intended to diagnose, treat, or cure any medical condition.
                    </p>
                </div>
            </section>

            {/* RITUAL — visual story */}
            <section data-testid="facials-ritual" className="relative py-32 bg-surface border-y border-hairline">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="reveal lg:col-span-5">
                        <p className="text-xs tracking-[0.3em] uppercase text-rose">The ritual</p>
                        <h2 className="font-serif text-5xl mt-3 leading-tight">Five steps. <span className="italic">No shortcuts.</span></h2>
                        <ol className="mt-8 space-y-5 text-white/80">
                            {[
                                "Skin consultation + double cleanse",
                                "Custom exfoliation (acid, enzyme, or physical — your skin's choice)",
                                "Steam, extractions, lymphatic drainage massage",
                                "Targeted serum + custom mask + LED therapy",
                                "SPF, finishing oil, and a quick at-home routine cheat-sheet",
                            ].map((s, i) => (
                                <li key={i} className="flex gap-4">
                                    <span className="font-serif text-rose text-2xl shrink-0 w-8">0{i + 1}</span>
                                    <span className="leading-relaxed">{s}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                    <div className="reveal lg:col-span-7 grid grid-cols-2 gap-3">
                        <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-white/10 col-span-1 row-span-2">
                            <img src={SHOT_1} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
                            <img src={SHOT_2} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
                            <img src={SHOT_3} alt="" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* MAKEUP CALLOUT */}
            <section data-testid="facials-makeup" className="relative py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="reveal lg:col-span-7">
                        <p className="text-xs tracking-[0.3em] uppercase text-gold">Professional makeup</p>
                        <h2 className="font-serif text-6xl mt-3 leading-tight">
                            Wedding-grade
                            <br />
                            makeup, <span className="italic text-rose">delivered.</span>
                        </h2>
                        <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-2xl">
                            Airbrush or traditional — long-wear, photo-tested pigments. Lashes included.
                            Perfect for weddings, birthdays, photoshoots, gala nights, or just a Saturday you want to feel cinematic.
                        </p>
                        <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-white/80 text-sm">
                            {[
                                "Bridal + bridal party packages",
                                "Photoshoot / editorial",
                                "Group bookings (3+ get a discount)",
                                "Lashes included on every full face",
                                "Pre-event facial pairings available",
                                "Touch-up kit on request",
                            ].map((f) => (
                                <li key={f} className="flex gap-2">
                                    <Check className="w-4 h-4 text-rose mt-0.5 shrink-0" /> {f}
                                </li>
                            ))}
                        </ul>
                        <Link
                            to={cta}
                            data-testid="facials-makeup-cta"
                            className="mt-10 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-rose text-ink hover:bg-roseHover transition font-medium"
                        >
                            Book makeup application <ChevronRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="reveal lg:col-span-5">
                        <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-white/10">
                            <img src={SHOT_2} alt="Pro makeup" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* TESTIMONIAL */}
            <section className="relative py-24 bg-surface border-y border-hairline">
                <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
                    <Quote className="w-10 h-10 text-rose/60 mx-auto" />
                    <p className="reveal font-serif text-3xl sm:text-4xl leading-relaxed mt-6 italic">
                        "I had my facial and makeup done at home before my wedding. Lashes, glow, everything — and the photographer kept asking who did it."
                    </p>
                    <div className="mt-8 flex items-center gap-3 justify-center">
                        <div className="w-10 h-10 rounded-full bg-rose/30 flex items-center justify-center font-serif text-rose">M</div>
                        <p className="text-white/70 text-sm">Bridal · Brooklyn</p>
                    </div>
                </div>
            </section>

            {/* CLOSING CTA */}
            <section data-testid="facials-cta" className="relative py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <p className="reveal text-xs tracking-[0.4em] uppercase text-rose">Book your glow</p>
                    <h2 className="reveal font-serif text-5xl sm:text-7xl mt-4 max-w-3xl leading-[1.05]">
                        Skin care that <span className="italic text-rose">comes to you.</span>
                    </h2>
                    <p className="reveal mt-6 max-w-xl text-white/70 text-lg">
                        Same-day appointments. Vegan products. Pro makeup artists on staff. Your living room is the new spa.
                    </p>
                    <div className="reveal mt-10 flex flex-wrap gap-4">
                        <Link
                            to={cta}
                            data-testid="facials-bottom-cta"
                            className="px-8 py-4 rounded-full bg-rose text-ink font-medium hover:bg-roseHover transition flex items-center gap-2"
                        >
                            Book Your Facial <ChevronRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/#plans"
                            className="px-8 py-4 rounded-full glass hover:bg-white/10 transition flex items-center gap-2"
                        >
                            See subscription plans <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
            <ConciergeChat />
        </div>
    );
}
